import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    OnInit,
    TemplateRef,
    ViewChild
} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {
    ConfiguratorDataViewService,
    ConfiguratorTableService,
    Constraint,
    DatastoreDto,
    DataView,
    DataViewDto,
    FieldTable,
    FieldTableDto,
    Partner,
    SaveQuery,
    SelectItem,
    Table,
    TableJointure
} from '@tj/tj-dmp-lib-api';
import {AccountService} from '../../auth/account.service';
import {CampaignFilterType, Field, Filter, InfoView, Query, StringFilterType} from '../../components/filter/filter.type';
import {parseType} from '../../components/filter/filter.utils';
import {FilterBuilderService} from '../../services/filter-builder.service';
import {TableMetaComponent} from '../../components/table-data/table-meta/table-meta.component';
import {FilterComponent} from '../../components/filter/filter.component';
import {Subject, Subscription} from 'rxjs';
import {TreeNode} from 'primeng/api';
import {ClassicTreeSave, FilterPresetComponent} from '../../components/filter/filter-preset/filter-preset.component';
import {TranslateService} from '@ngx-translate/core';
import {ConfirmationDialogComponent} from '../../components/confirmation-dialog/confirmation-dialog.component';
import {BeautifulFilterComponent} from "../../components/filter/beautiful-filter/beautiful-filter.component";
import {FilterResult} from "../../components/filter/filter-result.interface";
import {resolveI18nLabel} from '../../pipes/i18n-label.pipe';
import {LangService} from '../../services/lang.service';

@Component({
    selector: 'dmp-view',
    templateUrl: './view.component.html',
    styleUrls: ['./view.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false
})
export class ViewComponent implements OnInit, AfterViewInit {
    private readonly langService = inject(LangService);
    breadcrumb = [
    ];
    description: string;
    nameOfView = null;

    isSuperAdmin = false;
    datastore: string = null;
    partner: string = null;
    email: string = null;
    bookingRef: string = null;
    campaignCode: string = null;
    campaignFlux: string = null;

    criterTemplate: TemplateRef<unknown>;
    groupDataView: string = null;
    dataViewsList: SelectItem[] = [];
    dataView: DataView = null;
    dataViewFull: DataViewDto = null;
    display = false;

    dataViewLink: string = null;

    currentQuery: Query;

    selectedTables: string[] = [];
    selectedColumns: string[] = [];

    commandTable: string = null;

    tables: Table[] = [];
    private groupParam: string;
    private viewParam: string;
    private lockUpdateDataViews = false;

    private autoApplyFilter = false;
    private openDetailAfterFilter = false;
    private dataLoadedSubscription: Subscription = null;

    private changeDataViewsSubject$: Subject<{ source: 'datastore' | 'partner', data: any }>
        = new Subject<{ source: 'datastore' | 'partner', data: any }>();

    @ViewChild('table') table: TableMetaComponent;
    @ViewChild('filterPreset') presetFilter: FilterPresetComponent;
    @ViewChild('panelFilter') treeFilter: BeautifulFilterComponent;
    @ViewChild('filter') filter: FilterComponent;
    @ViewChild(ConfirmationDialogComponent) confirmationDialog: ConfirmationDialogComponent;
    // public currentFilter: Filter[];
    public currentTreeFilter: TreeNode<Filter>;
    presetSelected: ClassicTreeSave = null;
    private currentSaveQuery: SaveQuery;
    public currentTabParam: ClassicTreeSave;

    constructor(
        private readonly configuratorDataViewService: ConfiguratorDataViewService,
        private route: ActivatedRoute,
        private tableResourceService: ConfiguratorTableService,
        private filterBuilderService: FilterBuilderService,
        private ref: ChangeDetectorRef,
        private readonly translate: TranslateService,
        private router: Router,
        private readonly accountService: AccountService,
    ) {
        this.description = this.translate.instant('view.page.description.purchases');
        this.translate.onLangChange.subscribe(() => this.updateViewLabels());
    }

    get infoView(): InfoView {
        let havingCountTable: Table[] = [];
        if (this.dataViewFull && this.dataViewFull.havingCountTable) {
            havingCountTable = this.tables.filter(t => this.dataViewFull.havingCountTable.includes(t.code));
        }

        return {
            datastore: this.datastore,
            partner: this.partner,
            view: this.dataView.code,
            havingCountTable
        };
    }

    get fields() {
        if (!this.tables) {
            return [];
        }

        const fieldGroups = this.tables
            .filter(t => t.code !== 'personal_affi_auth')
            .map((table) => ({
                label: table && table.label && table.label.trim().length > 0 ? table.label : table.code,
                value: table.code,
                items: table.fields
                    .filter(f => f.categoryField !== FieldTable.CategoryFieldEnum.TECHNICAL)
                    .map((field) => ({
                        name: field.code,
                        label: field.label ?? field.code,
                        variableType: parseType(field.categoryField, field.type),
                        valueType: field.categoryField,
                        schema: table.code,
                    } as Field))
            }));

        // Ajout d'un champ virtuel pour les associations de campagne
        // uniquement sur les vues contacts et commandes
        const isPersonalView = this.groupDataView?.includes('personal');
        const isCommandView = this.groupDataView?.includes('command');

        if (isPersonalView || isCommandView) {
            const mainTableCode = isPersonalView ? 'personal' : 'command';

            fieldGroups.push({
                label: this.translate.instant('view.page.campaignGroupLabel'),
                value: '_campaign_association',
                items: [
                    {
                        name: '_campaign_flux',
                        label: this.translate.instant('view.page.campaignFields.flux'),
                        variableType: 'campaign' as any,
                        valueType: 'TEXT',
                        schema: mainTableCode
                    } as Field,
                    {
                        name: '_campaign_code',
                        label: this.translate.instant('view.page.campaignFields.code'),
                        variableType: 'campaign' as any,
                        valueType: 'TEXT',
                        schema: mainTableCode
                    } as Field,
                    {
                        name: '_campaign_name',
                        label: this.translate.instant('view.page.campaignFields.name'),
                        variableType: 'campaign' as any,
                        valueType: 'TEXT',
                        schema: mainTableCode
                    } as Field,
                    {
                        name: '_campaign_status',
                        label: this.translate.instant('view.page.campaignFields.status'),
                        variableType: 'campaign' as any,
                        valueType: 'TEXT',
                        schema: mainTableCode
                    } as Field,
                    {
                        name: '_campaign_contact_status',
                        label: this.translate.instant('view.page.campaignFields.contactStatus'),
                        variableType: 'campaign' as any,
                        valueType: 'TEXT',
                        schema: mainTableCode
                    } as Field,
                    {
                        name: '_campaign_utm_source',
                        label: this.translate.instant('view.page.campaignFields.utmSource'),
                        variableType: 'campaign' as any,
                        valueType: 'TEXT',
                        schema: mainTableCode
                    } as Field,
                    {
                        name: '_campaign_utm_medium',
                        label: this.translate.instant('view.page.campaignFields.utmMedium'),
                        variableType: 'campaign' as any,
                        valueType: 'TEXT',
                        schema: mainTableCode
                    } as Field,
                    {
                        name: '_campaign_utm_campaign',
                        label: this.translate.instant('view.page.campaignFields.utmCampaign'),
                        variableType: 'campaign' as any,
                        valueType: 'TEXT',
                        schema: mainTableCode
                    } as Field,
                    {
                        name: '_campaign_utm_term',
                        label: this.translate.instant('view.page.campaignFields.utmTerm'),
                        variableType: 'campaign' as any,
                        valueType: 'TEXT',
                        schema: mainTableCode
                    } as Field,
                    {
                        name: '_campaign_utm_content',
                        label: this.translate.instant('view.page.campaignFields.utmContent'),
                        variableType: 'campaign' as any,
                        valueType: 'TEXT',
                        schema: mainTableCode
                    } as Field
                ]
            });
        }

        return fieldGroups;
    }

    ngOnInit(): void {
        this.presetSelected = null;

        this.groupDataView = this.route.snapshot.paramMap.get('group');
        const queryParamsSnapshot = this.route.snapshot.queryParamMap;
        this.email = queryParamsSnapshot.get('email');
        this.bookingRef = queryParamsSnapshot.get('ref');
        this.campaignCode = queryParamsSnapshot.get('campaignCode');
        this.campaignFlux = queryParamsSnapshot.get('campaignFlux');
        this.viewParam = queryParamsSnapshot.get('code');
        this.groupParam = queryParamsSnapshot.get('group');
        this.datastore = queryParamsSnapshot.get('tenant');
        this.partner = queryParamsSnapshot.get('partner');
        this.openDetailAfterFilter = queryParamsSnapshot.get('detail') == 'true';

        if ((this.email || this.bookingRef || this.campaignCode || this.campaignFlux) && this.datastore && this.viewParam && this.partner) {
            this.autoApplyFilter = true;
        } else {
            if (this.groupParam) {
                this.datastore = this.groupParam;
            }
            this.viewParam = queryParamsSnapshot.get('view');
        }

        this.accountService.identity().then(account => {
            if (account) {
                this.isSuperAdmin = account.admin === true;
                if (!this.isSuperAdmin && !this.datastore && account.rules?.length > 0) {
                    this.datastore = account.rules[0].tenant;
                }
            }
            this.ref.detectChanges();
            this.updateDataViewsSequentially();
        });

        this.route.params.subscribe((el) => {
            const previousGroupDataView = this.groupDataView;
            this.groupDataView = el.group;
            if (previousGroupDataView != this.groupDataView) {
                this.updateViewLabels();
                // When switching between lead/personal (route param `group`),
                // the previously selected DataView (from query params) may no longer be relevant.
                // Reset selection so the new `group` drives the first matching DataView.
                this.viewParam = null;
                this.dataView = null;
                this.dataViewFull = null;
                this.tables = [];
                this.display = false;
                this.ref.detectChanges();

                this.updateDataViews();
            }
        });
    }

    private initCommandTable(): void {
        if (this.dataViewFull.table.tableName === 'command') {
            for (const table of this.dataViewFull.table.join) {
                if (['flight', 'carrental', 'hotel', 'parking'].includes(table.tableName)) {
                    this.commandTable = table.tableName;
                    break;
                }
                this.commandTable = null;
            }
        }
    }

    private updateViewLabels(): void {
        if (this.groupDataView?.includes('personal')) {
            this.translate.get(['menu.dataviews.label', 'menu.dataviews.contacts']).subscribe((texts) => {
                this.breadcrumb = [
                    { label: texts['menu.dataviews.label'] },
                    { label: texts['menu.dataviews.contacts'] }
                ];
                this.nameOfView = this.translate.instant('view.page.nameOfView.contact');
                this.description = this.translate.instant('view.page.description.contacts');
            });
        } else if (this.groupDataView?.includes('command')) {
            this.translate.get(['menu.dataviews.label', 'menu.dataviews.purchases']).subscribe((texts) => {
                this.breadcrumb = [
                    { label: texts['menu.dataviews.label'] },
                    { label: texts['menu.dataviews.purchases'] }
                ];
                this.nameOfView = this.translate.instant('view.page.nameOfView.purchase');
                this.description = this.translate.instant('view.page.description.purchases');
            });
        } else if (this.groupDataView?.includes('lead')) {
            this.translate.get(['menu.dataviews.label', 'menu.dataviews.leads']).subscribe((texts) => {
                this.breadcrumb = [
                    { label: texts['menu.dataviews.label'] },
                    { label: texts['menu.dataviews.leads'] }
                ];
                this.nameOfView = this.translate.instant('view.page.nameOfView.lead');
                this.description = this.translate.instant('view.page.description.leads');
            });
        }
    }


    ngAfterViewInit(): void {
        setTimeout(() => this.updateViewLabels(), 0);
    }

    onChangeDataStore($event: DatastoreDto): void {
        const datastore = $event ? $event.code : null;
        this.changeDataViewsSubject$.next({ source: 'datastore', data: datastore });
        this.treeFilter?.removeAll();
    }

    onChangePartner($event: Partner): void {
        const partner = $event ? $event.code : null;
        if (partner == null) {
            this.display = false;
        }
        this.changeDataViewsSubject$.next({ source: 'partner', data: partner });
    }

    updateDataViewsSequentially(): void {
        if (this.datastore && this.partner && !this.lockUpdateDataViews) {
            this.lockUpdateDataViews = true;
            this.configuratorDataViewService
                .getAllDataViewsUsingGET(this.datastore)
                .subscribe(data => this.handleDataViews(data));
            return;
        }
        this.changeDataViewsSubject$
            .subscribe(({ source, data }) => {
                if (source === 'datastore') {
                    this.datastore = data;
                    this.partner = null;
                    this.dataView = null;
                    this.dataViewsList = null;
                    this.display = false;
                    this.ref.detectChanges();
                }
                else if (source === 'partner') {
                    this.partner = data;
                }
                this.updateDataViews();
            });
    }


    parseFilter(filters: Filter[]): Constraint {
        return this.filterBuilderService.buildQuery(filters);
    }

    public updateDataView(): void {
        this.treeFilter?.removeAll();
        this.presetFilter?.clear();
        if (this.datastore && this.partner && this.dataView) {
            this.display = true;
            if (this.table) {
                this.resetFilters();
                this.filter?.resetFilters();
            }
            this.configuratorDataViewService.getDataViewUsingGET(this.datastore, this.partner, this.dataView.id, false)
                .subscribe(res => {
                    this.dataViewFull = res;
                    if (this.datastore) {
                        this.tableResourceService.getTablesUsingGET(this.datastore, res.tablesUsed).subscribe(result => {
                            this.tables = result;
                            this.ref.detectChanges();
                            if (this.autoApplyFilter) {
                                this.applyAutoFilter();
                            }
                        });
                    } else {
                        this.tables = [];
                        this.ref.detectChanges();
                    }
                    this.initCommandTable();
                });
        } else {
            this.tables = [];
            this.dataViewFull = null;
        }
        this.changeDataViewLink();
    }

    private changeDataViewLink(): void {
        if (this.datastore && this.dataView) {
            this.dataViewLink = `/h/settings/view-editor/${this.datastore}/${this.dataView.id}`;
        }
    }

    private handleDataViews(data: DataViewDto[]): void {
        this.lockUpdateDataViews = false;
        this.dataViewsList = data
            .sort((a, b) => a.code.localeCompare(b.code))
            .map((el) => ({
                label: resolveI18nLabel(el.labels, this.langService.currentLang, el.name),
                value: el,
            })).filter((d) => {
                const includes = d.value.code.includes(this.groupDataView);
                return includes;
            });

        if (this.viewParam) {
            this.dataView = this.dataViewsList.find(d => d.value.code === this.viewParam)?.value;
        }
        if (this.dataView == null && this.dataViewsList?.length > 0) {
            this.dataView = this.dataViewsList[0].value;
        }

        this.ref.detectChanges();
        if (this.dataView) {
            this.updateDataView();
        }
    }

    private updateDataViews(): void {
        if (this.datastore && !this.lockUpdateDataViews) {
            this.lockUpdateDataViews = true;
            this.configuratorDataViewService
                .getAllDataViewsUsingGET(this.datastore)
                .subscribe(data => this.handleDataViews(data));
        }
    }

    doSaveQueryChanged($event: ClassicTreeSave) {
        if ($event?.value) {
            if (this.treeFilter && this.treeFilter.updateSaveQuery) {
                const saveQuery: SaveQuery = {
                    id: $event.id,
                    datastore: this.datastore,
                    name: $event.name,
                    displayables: $event.cols,
                    orderings: $event.orderings as any,
                    partner: $event.partner,
                    affectedPartners: $event.affectedPartners,
                    isModel: $event.isModel,
                    query: null,
                    objectQueryJson: JSON.stringify($event.value),
                    view: this.dataView?.code,
                    having: $event.having
                };
                this.treeFilter.updateSaveQuery(saveQuery);
            }
        }
        this.currentSaveQuery = $event as any;
        // Mettre à jour presetSelected quand un preset est chargé
        this.presetSelected = $event;
        this.doSaveQueryUpdated({
            tabParam: $event
        });
    }

    doSaveQueryUpdated(filterResult: FilterResult) {
        this.currentTreeFilter = filterResult.filters || 
            (filterResult.tabParam?.value as TreeNode<Filter>) || 
            { 
            data: {} as Filter,
            children: []
            } as TreeNode<Filter>;
        if (filterResult.saveQuery) {
            this.currentSaveQuery = filterResult.saveQuery;
        }
        if (filterResult.tabParam) {
            this.currentTabParam = filterResult.tabParam;
        }
        if (this.currentTabParam?.cols && this.currentTabParam.cols.length > 0) {
            this.selectedTables = this.currentTabParam.cols.map(s => s.split('.')[0]);
            this.selectedColumns = this.currentTabParam.cols.map(s => s.split('.')[1]);
        }
        this.table.initPaginator(true);
        this.table.updateQuery(
            this.filterBuilderService.buildTreeQuery(this.currentTreeFilter),
            // this.filterBuilderService.buildOrderings(this.currentTabParam?.orderings),
            undefined,
            this.currentTabParam?.cols,
            this.currentTabParam?.having
        );
    }

    /** Préset retiré par la croix du sélecteur : ses filtres partent avec lui. Les colonnes
        affichées restent celles qui étaient sélectionnées. */
    doPresetUnloaded() {
        this.presetSelected = null;
        this.currentSaveQuery = null;
        this.treeFilter?.clearFilters();
    }

    doPresetSelectedUpdated($event: ClassicTreeSave) {
        console.log('doPresetSelectedUpdated');
        this.presetSelected = $event;
    }

    onSelectedColumnsChange(columns: FieldTableDto[]) {
        this.selectedTables = columns.map(c => c.table);
        this.selectedColumns = columns.map(c => c.code);
    }

    resetFilters() {
        this.currentTreeFilter = null;
        this.table.updateQuery(null, null);
    }

    openConfirmationDialog() {
        this.confirmationDialog.message = this.translate.instant('view.page.confirmLeave');
        this.confirmationDialog.showDialog();
    }

    onConfirm() {
        this.router.navigate(['/h/settings/view-editor', this.datastore, this.dataView.id]);
    }

    private applyAutoFilter(): void {
        if (!this.autoApplyFilter || !this.display || !this.treeFilter) {
            return;
        }

        if (this.campaignCode) {
            let codeField: Field = null;
            let fluxField: Field = null;

            for (const fieldGroup of this.fields) {
                if (!codeField) {
                    codeField = fieldGroup.items.find(item => item.name === '_campaign_code');
                }
                if (!fluxField) {
                    fluxField = fieldGroup.items.find(item => item.name === '_campaign_flux');
                }
            }

            if (!codeField) {
                console.error('Campaign code field not found for auto filter');
                this.autoApplyFilter = false;
                return;
            }

            const children: TreeNode<Filter<any>>[] = [];

            // Filter on campaign code: EXACTLY [campaignCode]
            children.push({
                data: {
                    value: [this.campaignCode],
                    filterType: {
                        code: StringFilterType.EXACTLY,
                        value: StringFilterType.EXACTLY,
                    },
                    field: codeField,
                    edit: false,
                } as Filter
            });

            // Optional filter on campaign flux (IMPORT / EXPORT) if provided and field exists.
            // API stores flux as IMPORT | EXPORT_SCENARIO | EXPORT_DIRECT (not plain EXPORT).
            if (this.campaignFlux && fluxField) {
                const fluxType = this.isCampaignExportFlux(this.campaignFlux)
                    ? CampaignFilterType.EXPORT
                    : CampaignFilterType.IMPORT;
                children.push({
                    data: {
                        value: null,
                        filterType: {
                            code: fluxType,
                            value: fluxType
                        },
                        field: fluxField,
                        edit: false
                    } as Filter
                });
            }

            const filterNode: TreeNode<Filter<any>> = {
                data: {
                    typeGroup: 'AND',
                    edit: false
                } as Filter,
                expanded: true,
                children: [
                    {
                        data: {
                            typeGroup: 'AND',
                            edit: false
                        } as Filter,
                        expanded: true,
                        children
                    }
                ]
            };

            this.treeFilter.setFilter(filterNode);
            this.autoApplyFilter = false;
        }

        else if (this.email || this.bookingRef) {
            const value: string = this.email || this.bookingRef;
            let field: Field = null;

            for (const fieldGroup of this.fields) {
                field = fieldGroup.items.find(item =>
                    (this.email && item.name === 'email') || (this.bookingRef && item.name === 'booking_reference')
                );

                if (field) {
                    break;
                }
            }

            if (!field) {
                console.error('Field not found for auto filter');
                this.autoApplyFilter = false;
                return;
            }

            const filterNode: TreeNode<Filter<any>> = {
                data: {
                    typeGroup: 'AND',
                    edit: false
                } as Filter,
                expanded: true,
                children: [
                    {
                        data: {
                            typeGroup: 'AND',
                            edit: false
                        } as Filter,
                        expanded: true,
                        children: [
                            {
                                data: {
                                    value: [value],
                                    filterType: {
                                        code: StringFilterType.EXACTLY,
                                        value: StringFilterType.EXACTLY,
                                    },
                                    field,
                                    edit: false,
                                } as Filter
                            }
                        ]
                    }
                ]
            };

            this.treeFilter.setFilter(filterNode);
            this.autoApplyFilter = false;
        }

        if (this.autoApplyFilter === false && this.openDetailAfterFilter && this.table) {
            this.dataLoadedSubscription = this.table.onDataLoaded.subscribe(data => {
                if (data && data.length > 0) {
                    this.openDetailForFirstResult();
                    this.dataLoadedSubscription.unsubscribe();
                    this.dataLoadedSubscription = null;
                }
            });
        }
    }

    private openDetailForFirstResult(): void {
        if (this.table && this.table.rowsData && this.table.rowsData.length > 0) {
            const firstRow = this.table.rowsData[0];
            this.table.openDetail(firstRow, 0);
            this.openDetailAfterFilter = false;
        }
    }

    /** True when campaign flux from URL/API is an export variant (scenario or direct). */
    private isCampaignExportFlux(flux: string): boolean {
        return flux === 'EXPORT_SCENARIO' || flux === 'EXPORT_DIRECT' || flux === 'EXPORT';
    }
}


